import {useAxiosClient} from "~/use/axios/client";
import {
    type GMServicesCommonQuestionsQuestionDefinition,
    type GMServicesCommonQuestionsQuestionTemplate,
    GMServicesCommonQuestionsQuestionTemplateType,
    QuestionApi
} from "~/gen/openapi/sblService";
import {MS_HOUR} from "~/constants/time";
import {keepPreviousData, useQuery} from "@tanstack/vue-query";
import type {FullContract} from "~/models/contract";
import {getSalesOrderTypeFromAllmaJobType} from "~/constants/salesOrder";
import {merge} from "lodash";

export const useQuestionService = () => {
    const {axiosClient} = useAxiosClient()
    const questionApi = new QuestionApi(undefined, '', axiosClient.value)

    const defaultQueryOptions = {
        staleTime: MS_HOUR,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        gcTime: Infinity,
    }

    const getQuestionTemplatesQueryOptions = {
        queryKey: ['questionGetAllQuestionTemplates'],
        queryFn: async () => {
            try {
                return (await questionApi.questionGetAllQuestionTemplates())?.data
            } catch {
                return []
            }
        },
        ...defaultQueryOptions
    }
    const questionTemplatesQuery = () => useQuery(getQuestionTemplatesQueryOptions)

    const getQuestionDefinitionsQueryOptions = {
        queryKey: ['questionGetAllQuestionDefinitions'],
        queryFn: async () => {
            try {
                return (await questionApi.questionGetAllQuestionDefinitions())?.data
            } catch {
                return []
            }
        },
        ...defaultQueryOptions
    }

    const questionDefinitionsQuery = () => useQuery(getQuestionDefinitionsQueryOptions)


    const getQuestionDefinitionById = (questionDefinitions: Array<GMServicesCommonQuestionsQuestionDefinition>, id: string) => {
        return questionDefinitions?.find((question) => question.Id === id)
    }

    const sortQuestionDefinitionsByOrder = (questionDefinitions: Array<GMServicesCommonQuestionsQuestionDefinition>) => {
        return questionDefinitions.sort((a, b) => {
            if (!a.Order && !b.Order) return 0;
            if (!a.Order) return 1;
            if (!b.Order) return -1;
            return a.Order - b.Order;
        })
    }

    const getQuestionTemplateByType = (questionTemplates: Array<GMServicesCommonQuestionsQuestionTemplate | undefined>, type: number, contract?: FullContract) => {
        const allmaJobTypesInContract = contract?.AllmaJobs?.map((aj) => aj.JobType || '0')
        // TODO: support action plans

        const salesOrderTypesInContract = allmaJobTypesInContract?.map((ajt) => getSalesOrderTypeFromAllmaJobType(ajt)) || []

        const templates = questionTemplates?.filter((template) => {
            return template?.QuestionTemplateType as unknown as number === type && template?.OrderTypes?.some((ot) => salesOrderTypesInContract?.includes(ot))
        })

        if (!!templates.length && templates.length > 1 ) {
            const mergedTemplates = merge({}, ...templates)
            return {
                Id: undefined,
                Name: null,
                QuestionTemplateType: type as unknown as GMServicesCommonQuestionsQuestionTemplateType,
                QuestionDefinitions: mergedTemplates.QuestionDefinitions as Array<GMServicesCommonQuestionsQuestionDefinition>,
                OrderTypes: null
            } as GMServicesCommonQuestionsQuestionTemplate
        }
        return templates[0]
    }

    return {
        getQuestionTemplatesQueryOptions,
        questionTemplatesQuery,
        getQuestionDefinitionsQueryOptions,
        questionDefinitionsQuery,
        getQuestionDefinitionById,
        sortQuestionDefinitionsByOrder,
        getQuestionTemplateByType,
    }
}